import {
    Cart,
    CartEntry,
    CustomerContractOption,
    ProductDeviceInCart,
    ProductVariant,
    VariantMatrix,
} from '@telialt/models/src/EshopModel';
import {
    selectFirstPurchasable,
    selectGiftEntry,
    selectIsFullPricePayment,
    selectLeasingPlan,
    selectProductInCart,
} from '@telialt/utils/src/eshopSelectors';
import {
    isEntryWithDevice,
    isEntryWithESimCard,
    isEntryWithGift,
    isEntryWithNetflix,
    isEntryWithRatePlan,
    isEntryWithSimCard,
    isEntryWithUpsell,
} from '@telialt/utils/src/eshopTypeGuards';
import { depthFirstSearch } from '@telialt/utils/src/eshopUtils';

import { DEFAULT_CURRENCY, DEFAULT_QUANTITY, normalizeNumber, normalizeSegment } from '../../core/normalizers';
import type { TrackingCommerce, TrackingItem } from '../../types';
import { GTMItemServiceType, GTMItemType, GTMOrderType } from '../../types';
import type { ProductUpsellEventInput, RemoveUpsellEventInput } from './types';

const trimLeadingZeros = (text: string) => text?.replace(/^0+/, '');

const getProductServiceType = (cart: Cart): GTMItemServiceType | '' => {
    if (cart?.customerContractOption === CustomerContractOption.Existing) {
        return GTMItemServiceType.Modification;
    }

    if (cart?.customerContractOption === CustomerContractOption.New) {
        return GTMItemServiceType.NewSale;
    }

    return '';
};

const isNonServiceEntry = (entry: CartEntry) =>
    isEntryWithDevice(entry) ||
    isEntryWithGift(entry) ||
    isEntryWithUpsell(entry) ||
    isEntryWithSimCard(entry) ||
    isEntryWithESimCard(entry);

const getChargedEntryTotal = (entry: CartEntry, isFullPricePayment: boolean, segment?: string) => {
    const chargeWithTax = normalizeSegment(segment) === 'B2B' && !entry.reverseVatApplied && isFullPricePayment;
    const netTotal = isFullPricePayment ? entry.totalPrice : entry.totalFullPriceWithDiscount;
    const taxInclusiveTotal = chargeWithTax ? entry.totalPriceWithTax : undefined;
    const chargedTotal = taxInclusiveTotal ?? netTotal;
    const copyrightFee =
        taxInclusiveTotal && entry.copyrightFeeWithTax ? entry.copyrightFeeWithTax : entry.copyrightFee;

    return normalizeNumber(chargedTotal?.value) + normalizeNumber(copyrightFee?.value);
};

const getEquipmentCartValue = (cart: Cart, segment?: string) => {
    const isFullPricePayment = selectIsFullPricePayment(cart);
    const itemsValue = (cart.entries || []).reduce((sum, entry) => {
        if (!isNonServiceEntry(entry)) {
            return sum;
        }

        return sum + getChargedEntryTotal(entry, isFullPricePayment, segment);
    }, 0);

    return itemsValue + normalizeNumber(cart.deliveryCost?.value);
};

export const getProductPageLeasingParams = (cart: Cart) => {
    const productEntry = selectFirstPurchasable(cart);
    const leasingPlan = selectLeasingPlan(cart);
    const periodFromLeasingPlanCode = parseInt(leasingPlan?.code?.split('_')?.[1] || '', 10) || 0;
    let paymentPeriod: number | undefined;

    if (productEntry?.rentingPlanEntry) {
        paymentPeriod = productEntry.rentingPlanEntry.monthValue;
    } else if (productEntry && !selectIsFullPricePayment(cart)) {
        paymentPeriod = leasingPlan?.durationInMonths || periodFromLeasingPlanCode;
    }

    if (paymentPeriod) {
        return {
            leasing_monthly_value: productEntry?.monthlyPrice?.value || 0,
            instant_value: cart?.selectedInitialPayment ?? 0,
            leasing_period: paymentPeriod,
        };
    }

    return {
        leasing_monthly_value: leasingPlan?.monthlyPrice?.value || 0,
        instant_value: cart?.selectedInitialPayment ?? 0,
        leasing_period: leasingPlan?.durationInMonths || 0,
    };
};

const getNextItemIndex = (entries: CartEntry[] | undefined) => {
    if (!entries?.length) {
        return 0;
    }

    return entries.reduce((max, entry) => Math.max(max, entry.entryNumber ?? 0), 0) + 1;
};

const getNetflixPrice = (entry: CartEntry) => entry?.monthlyPrice?.value;

const isFreeNetflixEntry = (entry: CartEntry) => {
    const netflixPrice = getNetflixPrice(entry);

    return isEntryWithNetflix(entry) && netflixPrice !== undefined && netflixPrice <= 0;
};

const normalizeCartEntryToTrackingItem = (entry: CartEntry, serviceType: GTMItemServiceType | ''): TrackingItem => {
    const isNetflix = isEntryWithNetflix(entry);
    const isNetflixGift = isFreeNetflixEntry(entry);
    const itemName = isNetflix ? 'Netflix' : entry.product?.name;
    const itemBrand = isNetflix ? 'Netflix' : entry.product?.brand;
    let itemCategory = entry.product?.category?.name;

    if (isNetflix) {
        itemCategory = 'OTT';
    } else if (isEntryWithRatePlan(entry)) {
        itemCategory = entry.product?.mobilityRatePlanType;
    }

    const itemListName = isNetflix ? 'OTT' : itemCategory;
    const netflixVariant = entry.product?.code === 'NETFLIX_PREMIUM_PRODUCT' ? 'Netflix Premium' : 'Netflix Standard';
    const productVariant = [entry.product?.productColor?.name, entry.product?.storageSize].filter(Boolean).join(' ');
    const itemVariant = isNetflix ? netflixVariant : productVariant;
    const price = isNetflix ? getNetflixPrice(entry) : entry.product?.price?.value;
    const currency = entry.product?.price?.currencyIso;
    const catalogProduct = entry.product as ProductDeviceInCart | undefined;
    const originalPrice = normalizeNumber(catalogProduct?.standardPrice?.value) || normalizeNumber(price);
    const isGift = isEntryWithGift(entry) || isNetflixGift;
    const item: TrackingItem = {
        id: trimLeadingZeros(entry.product?.code),
        currency,
        index: entry.entryNumber,
        name: itemName,
        brand: itemBrand,
        category: itemCategory,
        listId: catalogProduct?.category?.code,
        listName: itemListName,
        originalPrice,
        price,
        discount: 0,
        quantity: entry?.quantity ?? DEFAULT_QUANTITY,
        serviceType,
    };

    if (isGift) {
        item.type = GTMItemType.Gift;
    } else if (isEntryWithDevice(entry)) {
        item.type = GTMItemType.Product;
        item.nboDiscount = 0;
    }

    if (itemVariant) {
        item.variant = itemVariant;
    }

    return item;
};

export const mapProductPageCartToViewItemTrackingCommerce = (
    cart: Cart,
    segment?: string,
): TrackingCommerce | undefined => {
    const productEntry = selectFirstPurchasable(cart);
    const product = productEntry?.product;

    if (!productEntry || !product) {
        return undefined;
    }

    const price = normalizeNumber(product.price?.value);
    const originalPrice = normalizeNumber(product.standardPrice?.value) || price;
    const quantity = productEntry.quantity || DEFAULT_QUANTITY;
    const categoryName = product.category?.name || '';
    const copyrightFee = normalizeNumber(productEntry.copyrightFee?.value);

    return {
        orderType: GTMOrderType.Equipment,
        segment: segment ? normalizeSegment(segment) : '',
        currency: product.price?.currencyIso || DEFAULT_CURRENCY,
        value: price * quantity + copyrightFee,
        itemListName: categoryName,
        giftIncluded: false,
        coupon: '',
        paymentType: '',
        paymentMethod: '',
        signMethod: '',
        bank: '',
        transactionId: '',
        items: [
            {
                type: GTMItemType.Product,
                id: trimLeadingZeros(product.code),
                name: product.name || '',
                brand: product.brand || '',
                variant: [product.productColor?.name, product.storageSize].filter(Boolean).join(' '),
                category: categoryName,
                listId: product.category?.code || '',
                listName: categoryName,
                index: 0,
                originalPrice,
                price,
                nboDiscount: 0,
                discount: 0,
                quantity,
            },
        ],
    };
};

export const mapProductPageCartToAddToCartTrackingCommerce = (cart: Cart, segment?: string): TrackingCommerce => {
    const serviceType = getProductServiceType(cart);
    const items = cart?.entries?.map((entry) => normalizeCartEntryToTrackingItem(entry, serviceType)) || [];
    const mainProduct = selectProductInCart(cart);

    if (mainProduct?.insurancePossible && cart?.withInsurance) {
        const price = cart?.insuranceMonthlyPrice;
        const insurancePrice = normalizeNumber(price?.value);

        items.push({
            type: GTMItemType.Product,
            index: getNextItemIndex(cart?.entries),
            id: 'Insurance',
            name: 'Insurance',
            variant: 'Insurance',
            brand: 'Insurance',
            category: 'Insurance',
            listId: mainProduct?.category?.code || '',
            listName: mainProduct?.category?.name,
            currency: price?.currencyIso,
            originalPrice: insurancePrice,
            price: insurancePrice,
            discount: 0,
            quantity: DEFAULT_QUANTITY,
        });
    }

    const cartItems = cart?.entries || items.length ? items : undefined;
    const hasMainPurchasableProduct = Boolean(mainProduct);

    return {
        orderType: hasMainPurchasableProduct ? GTMOrderType.Equipment : GTMOrderType.Services,
        segment: segment ? normalizeSegment(segment) : '',
        currency: cart.totalPriceWithTax?.currencyIso || DEFAULT_CURRENCY,
        value: getEquipmentCartValue(cart, segment),
        giftIncluded: Boolean(selectGiftEntry(cart) || cart?.entries?.some(isFreeNetflixEntry)),
        coupon: '',
        paymentType: selectIsFullPricePayment(cart) ? 'Full Price' : 'Installment',
        paymentMethod: '',
        signMethod: '',
        bank: '',
        transactionId: '',
        itemListName: cartItems && cartItems.length > 1 ? 'Mixed' : cartItems?.[0]?.listName,
        items: cartItems,
    };
};

const getUpsellVariant = (product: ProductDeviceInCart | ProductVariant): string => {
    const productVariant = product as ProductVariant;
    const variantFromProduct = [product?.productColor?.name, product?.storageSize].filter(Boolean).join(' ');

    if (variantFromProduct) {
        return variantFromProduct;
    }

    const byProductCode = (element: VariantMatrix) => element.isLeaf && element.variantOption.code === product?.code;
    const productFromMatrix = depthFirstSearch(productVariant?.variantMatrix, byProductCode);

    return productFromMatrix?.variantValueCategory?.name || '';
};

const normalizeProductDeviceToTrackingItem = (
    product: ProductDeviceInCart | ProductVariant,
    index: number,
): TrackingItem => {
    const productVariant = product as ProductVariant;
    const itemCategory = product?.category?.name || productVariant?.categoryName;

    return {
        id: trimLeadingZeros(product?.code),
        currency: product?.price?.currencyIso,
        index,
        brand: product?.brand,
        name: product?.name,
        category: itemCategory,
        listName: itemCategory,
        variant: getUpsellVariant(product),
        price: normalizeNumber(product?.price?.value),
        quantity: DEFAULT_QUANTITY,
        funnelCategory: itemCategory,
        funnelVariant: 'Priedai',
    };
};

const mapProductUpsellToTrackingCommerce = (
    data: { product: ProductDeviceInCart | ProductVariant; segment?: string },
    index: number,
): TrackingCommerce => {
    const item = normalizeProductDeviceToTrackingItem(data.product, index);

    return {
        segment: data.segment,
        currency: item.currency,
        value: item.price ?? 0,
        itemListName: item.listName,
        items: [item],
    };
};

export const mapProductUpsellToAddToCartTrackingCommerce = (
    data: ProductUpsellEventInput['data'],
): TrackingCommerce => {
    const { cart, product, segment } = data;
    const productVariant = product as ProductVariant;
    const price = normalizeNumber(product?.price?.value);
    const standardPrice = normalizeNumber(product?.standardPrice?.value);
    const categoryName = product?.category?.name || productVariant?.categoryName || '';
    const categoryCode = product?.category?.code || '';
    const variant = getUpsellVariant(product);

    return {
        orderType: GTMOrderType.Equipment,
        segment: segment ? normalizeSegment(segment) : '',
        currency: product?.price?.currencyIso || DEFAULT_CURRENCY,
        value: price,
        itemListName: categoryName,
        giftIncluded: false,
        coupon: '',
        paymentType: selectIsFullPricePayment(cart) ? 'Full Price' : 'Installment',
        paymentMethod: '',
        signMethod: '',
        bank: '',
        transactionId: '',
        items: [
            {
                type: GTMItemType.Product,
                id: trimLeadingZeros(product?.code) || '',
                name: product?.name || '',
                brand: product?.brand || '',
                variant,
                category: categoryName,
                listId: categoryCode,
                listName: categoryName,
                index: 0,
                originalPrice: standardPrice > price ? standardPrice : price,
                price,
                discount: 0,
                quantity: DEFAULT_QUANTITY,
            },
        ],
    };
};

export const mapProductUpsellToRemoveFromCartTrackingCommerce = (
    data: RemoveUpsellEventInput['data'],
): TrackingCommerce =>
    mapProductUpsellToAddToCartTrackingCommerce({
        cart: data.cart,
        product: data.entry.product,
        segment: data.segment,
    });

export const mapProductUpsellToItemTrackingCommerce = (data: ProductUpsellEventInput['data']): TrackingCommerce =>
    mapProductUpsellToTrackingCommerce(data, 0);

export const mapProductInsuranceToTrackingCommerce = (cart: Cart): TrackingCommerce => {
    const mainProduct = selectProductInCart(cart);
    const price = cart?.insuranceMonthlyPrice;

    return {
        currency: price?.currencyIso,
        value: 0,
        items: [
            {
                index: cart?.entries?.length,
                id: 'Insurance',
                name: 'Insurance',
                variant: 'Insurance',
                brand: 'Insurance',
                category: 'Insurance',
                listName: mainProduct?.category?.name,
                currency: price?.currencyIso,
                price: price?.value,
                quantity: DEFAULT_QUANTITY,
            },
        ],
    };
};
